// HoCarousel.js
import React, { useState, useEffect } from 'react';

const styles = {
  heroSection: {
    position: 'relative',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  heroSlide: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transition: 'opacity 0.8s ease-in-out',
  },
  // Reemplaza backgroundImage — el browser lo descubre en el HTML scan,
  // no después de calcular CSSOM+layout.
  heroImg: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    objectPosition: 'center',
  },
  heroOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background:
      'linear-gradient(135deg, rgba(102,126,234,0.8) 0%, rgba(118,75,162,0.8) 100%)',
  },
  heroContent: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    color: 'white',
    padding: '16px',
  },
  heroTitle: {
    fontSize: '3.5rem',
    fontWeight: 'bold',
    marginBottom: '24px',
    textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
  },
  heroDescription: {
    fontSize: '1.4rem',
    marginBottom: '32px',
    maxWidth: '700px',
    lineHeight: '1.6',
    textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
  },
  emojiHero: {
    fontSize: '4rem',
    marginBottom: '20px',
  },
  heroButton: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '16px 32px',
    background: 'linear-gradient(45deg, #ff6b6b, #feca57)',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '50px',
    fontWeight: '600',
    fontSize: '1.1rem',
    transition: 'all 0.3s ease',
    boxShadow: '0 8px 25px rgba(0,0,0,0.2)',
    border: 'none',
    cursor: 'pointer',
  },
  indicators: {
    position: 'absolute',
    bottom: '24px',
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    gap: '12px',
    zIndex: 2,
  },
  indicator: {
    width: '14px',
    height: '14px',
    backgroundColor: 'rgba(255,255,255,0.5)',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  activeIndicator: {
    backgroundColor: 'white',
    transform: 'scale(1.2)',
  },
};

function HoCarousel({ carouselItems, height = '90vh' }) {
  const [activeSlide, setActiveSlide] = useState(0);
  const [buttonHovered, setButtonHovered] = useState(false);
  const [imgErrors, setImgErrors] = useState({});

  /**
   * Inyecta <link rel="preload"> para la primera imagen lo antes posible.
   * Esto le da al browser una pista explícita para que la descargue con
   * alta prioridad antes incluso de que React haya renderizado el <img>.
   */
  useEffect(() => {
    if (!carouselItems?.length) return;
    const { imageUrl } = carouselItems[0];
    if (!imageUrl) return;

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = imageUrl;
    document.head.appendChild(link);

    return () => {
      if (document.head.contains(link)) document.head.removeChild(link);
    };
  }, [carouselItems]);

  // Auto-play
  useEffect(() => {
    if (!carouselItems || carouselItems.length <= 1) return;
    const id = setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % carouselItems.length);
    }, 5000);
    return () => clearInterval(id);
  }, [carouselItems]);

  if (!carouselItems?.length) return null;

  return (
    <div style={{ ...styles.heroSection, height }}>
      {carouselItems.map((item, index) => {
        const isFirst = index === 0;

        return (
          <div
            key={item.id}
            style={{
              ...styles.heroSlide,
              opacity: activeSlide === index ? 1 : 0,
              pointerEvents: activeSlide === index ? 'auto' : 'none',
              // Fallback de color mientras carga (o si falla)
              backgroundColor: item.fallbackColor,
            }}
          >
            {/*
             * <img> real en lugar de backgroundImage:
             *   - El preload scanner del browser lo detecta en el HTML inicial
             *   - Permite fetchPriority y loading nativos
             *   - Es candidato LCP válido (backgroundImage no lo es)
             *
             * alt="" + aria-hidden porque es decorativa; el texto del h1 da contexto.
             */}
            {!imgErrors[index] && (
              <img
                src={item.imageUrl}
                alt=""
                aria-hidden="true"
                style={styles.heroImg}
                loading={isFirst ? 'eager' : 'lazy'}
                fetchPriority={isFirst ? 'high' : 'auto'}
                decoding="async"
                onError={() =>
                  setImgErrors((prev) => ({ ...prev, [index]: true }))
                }
              />
            )}

            <div style={styles.heroOverlay} />

            <div style={styles.heroContent}>
              <div style={styles.emojiHero}>{item.emoji}</div>
              <h1 style={styles.heroTitle}>{item.title}</h1>
              <p style={styles.heroDescription}>{item.description}</p>
              <a
                href="#register"
                style={{
                  ...styles.heroButton,
                  ...(buttonHovered
                    ? {
                        transform: 'translateY(-3px)',
                        boxShadow: '0 12px 35px rgba(0,0,0,0.3)',
                      }
                    : {}),
                }}
                onMouseEnter={() => setButtonHovered(true)}
                onMouseLeave={() => setButtonHovered(false)}
                onClick={(e) => {
                  e.preventDefault();
                  window.location.hash = 'register';
                }}
              >
                🚀 Start Now
                <svg
                  style={{ marginLeft: '12px' }}
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </a>
            </div>
          </div>
        );
      })}

      {carouselItems.length > 1 && (
        <div style={styles.indicators}>
          {carouselItems.map((_, index) => (
            <button
              key={index}
              style={{
                ...styles.indicator,
                ...(activeSlide === index ? styles.activeIndicator : {}),
              }}
              onClick={() => setActiveSlide(index)}
              aria-label={`Ir al slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default HoCarousel;